import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import DrawIcon from '@mui/icons-material/Draw'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import SignatureCanvas from 'react-signature-canvas'
import { supabase } from '../utils/supabase'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const fmt = (ts) => ts ? dayjs(ts).format('MMMM D, YYYY, hh:mm A') : '—'

export default function AgreementSignPage() {
	const { signedAgreementId } = useParams()
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

	// ── Auth / loading state ───────────────────────────────────────────────────
	const [session, setSession] = useState(null)
	const [pageState, setPageState] = useState('loading') // 'loading' | 'notfound' | 'view' | 'edit' | 'expired'
	const [loadError, setLoadError] = useState('')

	// ── Data ──────────────────────────────────────────────────────────────────
	const [signedAgreement, setSignedAgreement] = useState(null)  // signed_agreements row
	const [agreement, setAgreement] = useState(null)              // agreements row
	const [merchant, setMerchant] = useState(null)                // merchant row (name, logo)
	const [merchantLogoUrl, setMerchantLogoUrl] = useState(null)

	// ── PDF ───────────────────────────────────────────────────────────────────
	const [pdfUrl, setPdfUrl] = useState(null)
	const [pdfNumPages, setPdfNumPages] = useState(null)
	const [pdfLoading, setPdfLoading] = useState(false)

	// ── Signature (view) ──────────────────────────────────────────────────────
	const [signatureUrl, setSignatureUrl] = useState(null)

	// ── Sign (edit mode) ──────────────────────────────────────────────────────
	const sigPadRef = useRef(null)
	const contentScrollRef = useRef(null)
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
	const [signStep, setSignStep] = useState('read') // 'read' | 'sign'
	const [saving, setSaving] = useState(false)
	const [saveError, setSaveError] = useState('')

	// ── Step 1: ensure session ────────────────────────────────────────────────
	useEffect(() => {
		let mounted = true
		const init = async () => {
			const { data } = await supabase.auth.getSession()
			if (!mounted) return
			if (data.session) {
				setSession(data.session)
			} else {
				// Anonymous sign-in to allow storage access
				const { data: anonData, error } = await supabase.auth.signInAnonymously()
				if (!mounted) return
				if (error) { setLoadError('Unable to initialize session.'); setPageState('notfound'); return }
				setSession(anonData.session)
			}
		}
		init()
		return () => { mounted = false }
	}, [])

	// ── Step 2: load data once session exists ────────────────────────────────
	useEffect(() => {
		if (!session || !signedAgreementId) return
		loadData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session, signedAgreementId])

	const loadData = async () => {
		try {
			// Fetch the signed_agreement row + nested agreement
			const { data: sa, error: saErr } = await supabase
				.from('signed_agreements')
				.select(`
					id, merchant_id, client_id, agreement_id,
					status, signature_path, signed_at, created_at, expiry_date,
					agreements ( id, title, content, content_path )
				`)
				.eq('id', signedAgreementId)
				.single()

			if (saErr || !sa) { setPageState('notfound'); return }

			// Fetch merchant for display only
			const { data: merch } = await supabase
				.from('merchant')
				.select('id, name')
				.eq('id', sa.merchant_id)
				.single()

			setSignedAgreement(sa)
			setAgreement(sa.agreements)
			setMerchant(merch || null)

			// Merchant logo from merchant_photos (is_avatar = true)
			const { data: logoRow } = await supabase
				.from('merchant_photos')
				.select('path, bucket')
				.eq('merchant_id', sa.merchant_id)
				.eq('is_avatar', true)
				.maybeSingle()

			if (logoRow?.path) {
				const { data: logoData } = await supabase.storage
					.from(logoRow.bucket || 'merchant-photos')
					.createSignedUrl(logoRow.path, 60 * 60)
				if (logoData?.signedUrl) setMerchantLogoUrl(logoData.signedUrl)
			}

			// Load PDF if applicable
			if (sa.agreements?.content_path) {
				setPdfLoading(true)
				try {
					const { data: fileData } = await supabase.storage
						.from('agreements')
						.download(sa.agreements.content_path)
					if (fileData) setPdfUrl(URL.createObjectURL(fileData))
				} catch { /* ignore */ } finally {
					setPdfLoading(false)
				}
			}

			// Determine page state based purely on record status
			const isExpired = sa.expiry_date && dayjs().isAfter(dayjs(sa.expiry_date))

			if (sa.status === 'signed') {
				if (sa.signature_path) {
					const { data: sigData } = await supabase.storage
						.from('agreement-signatures')
						.createSignedUrl(sa.signature_path, 60 * 60)
					if (sigData?.signedUrl) setSignatureUrl(sigData.signedUrl)
				}
				setPageState('view')
			} else if (sa.status === 'pending') {
				setPageState(isExpired ? 'expired' : 'edit')
			} else {
				setPageState('view')
			}
		} catch (err) {
			console.error('Failed to load agreement page:', err)
			setPageState('notfound')
		}
	}

	// ── Signature canvas resize fix ──────────────────────────────────────────
	// react-signature-canvas keeps default 300×150 internal resolution when CSS
	// stretches it to width:100%, causing stroke coordinates to be offset.
	useEffect(() => {
		if (signStep !== 'sign') return
		const resizeCanvas = () => {
			const pad = sigPadRef.current
			if (!pad) return
			const canvas = pad.getCanvas()
			const rect = canvas.getBoundingClientRect()
			if (rect.width === 0) return
			canvas.width = Math.round(rect.width)
			canvas.height = Math.round(rect.height)
		}
		const raf = requestAnimationFrame(resizeCanvas)
		window.addEventListener('resize', resizeCanvas)
		return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resizeCanvas) }
	}, [signStep])

	// ── Scroll-to-bottom logic ───────────────────────────────────────────────
	useEffect(() => {
		if (pageState !== 'edit' || hasScrolledToBottom) return
		const timer = setTimeout(() => {
			const el = contentScrollRef.current
			if (el && el.scrollHeight <= el.clientHeight + 24) {
				setHasScrolledToBottom(true)
			}
		}, 200)
		return () => clearTimeout(timer)
	}, [pageState, hasScrolledToBottom, pdfNumPages, agreement])

	const handleContentScroll = useCallback((e) => {
		if (hasScrolledToBottom) return
		const el = e.currentTarget
		if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
			setHasScrolledToBottom(true)
		}
	}, [hasScrolledToBottom])

	// ── Save signature ────────────────────────────────────────────────────────
	const handleSave = async () => {
		if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
			setSaveError('Please draw your signature before saving.')
			return
		}
		setSaving(true)
		setSaveError('')
		try {
			const fileId = uuidv4()
			const storagePath = `${signedAgreement.merchant_id}/${fileId}.png`

			const dataUrl = sigPadRef.current.toDataURL('image/png')
			const blob = await fetch(dataUrl).then(r => r.blob())

			const { error: uploadErr } = await supabase.storage
				.from('agreement-signatures')
				.upload(storagePath, blob, { contentType: 'image/png' })
			if (uploadErr) throw uploadErr

			const signedAt = new Date().toISOString()
			const { error: updateErr } = await supabase
				.from('signed_agreements')
				.update({
					status: 'signed',
					signed_at: signedAt,
					signature_path: storagePath,
				})
				.eq('id', signedAgreementId)
			if (updateErr) throw updateErr

			// Skip the success screen and switch to the signed view directly.
			// Use the in-memory data URL for the signature image so it renders
			// instantly without needing a round-trip to fetch a signed URL.
			setSignedAgreement(prev => prev ? {
				...prev,
				status: 'signed',
				signed_at: signedAt,
				signature_path: storagePath,
			} : prev)
			setSignatureUrl(dataUrl)
			setPageState('view')
		} catch (err) {
			setSaveError('Failed to save: ' + (err.message || 'Unknown error'))
		} finally {
			setSaving(false)
		}
	}

	// ── Derived helpers ───────────────────────────────────────────────────────
	// Step 1: need scroll to continue; Step 2: no scroll check needed
	const canContinue = hasScrolledToBottom
	const canSave = !saving

	// ── Shared content renderer ───────────────────────────────────────────────
	const renderAgreementContent = () => {
		if (agreement?.content_path) {
			return pdfLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
					<CircularProgress size={28} sx={{ color: '#9F7AEA' }} />
				</Box>
			) : pdfUrl ? (
				<Document file={pdfUrl} onLoadSuccess={({ numPages }) => setPdfNumPages(numPages)}
					loading={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} sx={{ color: '#9F7AEA' }} /></Box>}>
					{Array.from({ length: pdfNumPages || 0 }, (_, i) => (
						<Box key={i + 1} sx={{ mb: '8px' }}>
							<Page pageNumber={i + 1} width={isMobile ? 280 : 660}
								renderTextLayer={false} renderAnnotationLayer={false} />
						</Box>
					))}
				</Document>
			) : (
				<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#718096', textAlign: 'center', py: 3 }}>
					Unable to load PDF.
				</Typography>
			)
		}
		return (
			<Box
				sx={{ fontFamily: 'Inter', fontSize: '14px', lineHeight: 1.7, color: '#4A5568', '& h1,& h2,& h3': { fontWeight: 600, mb: 1 }, '& p': { mb: 1 }, '& ul,& ol': { pl: 2, mb: 1 } }}
				dangerouslySetInnerHTML={{ __html: agreement?.content || '' }}
			/>
		)
	}

	// ── Background decoration ─────────────────────────────────────────────────
	const BgGradient = () => (
		<>
			<Box sx={{ position: 'fixed', top: -600, left: -400, width: 1400, height: 1400, background: 'radial-gradient(circle, #C4A8F5 0%, #A78BF0 60%, transparent 100%)', filter: 'blur(120px)', opacity: 0.45, pointerEvents: 'none', zIndex: 0 }} />
			<Box sx={{ position: 'fixed', bottom: -300, right: -200, width: 1100, height: 1100, background: 'radial-gradient(circle, #F8A8A0 0%, #F28080 60%, transparent 100%)', filter: 'blur(130px)', opacity: 0.38, pointerEvents: 'none', zIndex: 0 }} />
		</>
	)

	// ── States ────────────────────────────────────────────────────────────────
	if (pageState === 'loading') {
		return (
			<Box sx={{ minHeight: '100%', bgcolor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<BgGradient />
				<CircularProgress sx={{ color: '#9F7AEA', zIndex: 1 }} />
			</Box>
		)
	}

	if (pageState === 'notfound') {
		return (
			<Box sx={{ minHeight: '100%', bgcolor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
				<BgGradient />
				<Box sx={{ textAlign: 'center', zIndex: 1 }}>
					<Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '24px', color: '#2D3748', mb: 1 }}>Agreement not found</Typography>
					<Typography sx={{ fontFamily: 'Inter', fontSize: '15px', color: '#718096' }}>
						{loadError || 'This agreement link is invalid or you do not have access.'}
					</Typography>
				</Box>
			</Box>
		)
	}

	if (pageState === 'expired') {
		return (
			<Box sx={{ minHeight: '100%', bgcolor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
				<BgGradient />
				<Box sx={{ textAlign: 'center', zIndex: 1 }}>
					<Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '24px', color: '#2D3748', mb: 1 }}>Agreement expired</Typography>
					<Typography sx={{ fontFamily: 'Inter', fontSize: '15px', color: '#718096' }}>
						This agreement has expired. Please contact the merchant for a new link.
					</Typography>
				</Box>
			</Box>
		)
	}

	const isEditMode = pageState === 'edit'
	const isSigned = signedAgreement?.status === 'signed'

	// Sticky panel height estimate for bottom padding in edit mode
	const PANEL_HEIGHT = isMobile ? 260 : 300

	const Navbar = () => (
		<Box sx={{ position: 'relative', zIndex: 1, bgcolor: 'transparent', px: '78px', py: '12px', height: 91, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
				{merchantLogoUrl ? (
					<Box component="img" src={merchantLogoUrl} alt="logo" sx={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
				) : (
					<Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#D9D9D9', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '22px', color: '#666' }}>{merchant?.name?.[0] || 'M'}</Typography>
					</Box>
				)}
				<Typography sx={{ fontFamily: 'Nunito, Inter, sans-serif', fontWeight: 700, fontSize: isMobile ? '24px' : '36px', color: '#000', lineHeight: 1 }}>
					{merchant?.name || ''}
				</Typography>
			</Box>
		</Box>
	)

	// ── Shared JSX fragments (not components — avoids unmount/remount on re-render) ──
	const agreementInfoJsx = (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
			<Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '24px', color: '#000', lineHeight: 1.21 }}>
				{agreement?.title || '—'}
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
				<SendIcon sx={{ fontSize: 19, color: 'rgba(0,0,0,0.6)' }} />
				<Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '16px', color: 'rgba(0,0,0,0.6)' }}>
					Sent {fmt(signedAgreement?.created_at)}
				</Typography>
			</Box>
		</Box>
	)

	// contentBoxJsx(sxOverride) — inline JSX so the DOM node is never remounted
	const contentBoxJsx = (sxOverride) => (
		<Box
			ref={contentScrollRef}
			onScroll={handleContentScroll}
			sx={{ border: '1px solid rgba(0,0,0,0.3)', borderRadius: '12px', height: 550, overflow: 'auto', p: '16px 20px', ...sxOverride }}
		>
			{renderAgreementContent()}
		</Box>
	)

	// ── EDIT mode ────────────────────────────────────────────────────────────
	if (isEditMode) {
		// ── Step 1: Read ──────────────────────────────────────────────────────
		if (signStep === 'read') {
			return (
				<Box sx={{ height: '100%', bgcolor: '#FFFFFF', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
					<BgGradient />
					<Navbar />

					{/* Main area — fills remaining height, no page scroll */}
					<Box sx={{ position: 'relative', zIndex: 1, flex: 1, overflow: 'hidden', display: 'flex', justifyContent: 'center', p: isMobile ? '16px' : '32px', pb: isMobile ? '16px' : '24px', gap: '20px' }}>
						<Box sx={{ width: '100%', maxWidth: 750, height: '100%' }}>
							{/* Card — fills available space, button inside at bottom */}
							<Box sx={{ height: '100%', overflow: 'hidden', bgcolor: '#FFFFFF', borderRadius: '16px', boxShadow: '0px 4px 20px 0px rgba(0,0,0,0.25)', p: isMobile ? '24px 16px' : '40px 30px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
								{agreementInfoJsx}
								{/* content box fills remaining card height */}
								{contentBoxJsx({ flex: 1, height: 'auto' })}
								{/* Continue button — inside card, right-aligned */}
								<Box sx={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
									<Button
										variant="contained"
										disabled={!canContinue}
										onClick={() => setSignStep('sign')}
										sx={{
											textTransform: 'none',
											fontFamily: 'Inter',
											fontWeight: 600,
											fontSize: '18px',
											lineHeight: '22px',
											px: '40px',
											py: '20px',
											borderRadius: '30px',
											bgcolor: '#C85AFF',
											border: '1px solid #9F7AEA',
											opacity: canContinue ? 1 : 0.6,
											'&:hover': { bgcolor: '#B521FF' },
											'&:disabled': { bgcolor: '#C85AFF', color: '#FFFFFF', opacity: 0.6 },
										}}
									>
										Continue to sign agreement
									</Button>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			)
		}

		// ── Step 2: Sign ──────────────────────────────────────────────────────
		return (
			<Box sx={{ minHeight: '100%', bgcolor: '#FFFFFF', display: 'flex', flexDirection: 'column', position: 'relative' }}>
				<BgGradient />
				<Navbar />

				{/* Scrollable content — extra bottom padding so content clears the sticky panel */}
				<Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', justifyContent: 'center', p: isMobile ? '16px' : '32px', pb: `${PANEL_HEIGHT + 32}px` }}>
					<Box sx={{ width: '100%', maxWidth: 750, bgcolor: '#FFFFFF', borderRadius: '16px', boxShadow: '0px 4px 20px 0px rgba(0,0,0,0.25)', p: isMobile ? '24px 16px' : '40px 30px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
						{agreementInfoJsx}
						{contentBoxJsx()}
					</Box>
				</Box>

				{/* ── Sticky signature panel ── */}
				<Box sx={{
					position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
					bgcolor: '#FFFFFF',
					borderTop: '1px solid rgba(0,0,0,0.2)',
					p: isMobile ? '12px 16px 20px' : '16px',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '14px',
				}}>
					<Box sx={{ width: '100%', maxWidth: 750, display: 'flex', flexDirection: 'column', gap: '14px' }}>
						{/* Signature canvas box */}
						<Box sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: '12px', p: '10px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
								<Typography sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: 'rgba(0,0,0,0.5)' }}>
									Provide your digital signature:
								</Typography>
								<Button size="small" onClick={() => sigPadRef.current?.clear()}
									sx={{ textTransform: 'none', fontFamily: 'Inter', fontSize: '12px', color: '#718096', p: '2px 8px', minWidth: 0 }}>
									Clear
								</Button>
							</Box>
							<Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', bgcolor: '#FFFFFF', overflow: 'hidden', touchAction: 'none' }}>
								<SignatureCanvas
									ref={sigPadRef}
									penColor="#1A202C"
									canvasProps={{ style: { width: '100%', height: 150, display: 'block' } }}
								/>
							</Box>
							{saveError && (
								<Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#E53E3E' }}>{saveError}</Typography>
							)}
						</Box>

						{/* Buttons row */}
						<Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center', px: isMobile ? '8px' : '50px' }}>
							<Button
								onClick={() => setSignStep('read')}
								sx={{
									flex: 1,
									textTransform: 'none',
									fontFamily: 'Inter',
									fontWeight: 600,
									fontSize: '16px',
									py: '14px',
									borderRadius: '30px',
									border: '1px solid #CBD5E0',
									color: '#9F7AEA',
									bgcolor: '#FFFFFF',
									'&:hover': { bgcolor: '#F7FAFC' },
								}}
							>
								Back
							</Button>
							<Button
								variant="contained"
								disabled={!canSave}
								onClick={handleSave}
								sx={{
									flex: 1,
									textTransform: 'none',
									fontFamily: 'Inter',
									fontWeight: 600,
									fontSize: '16px',
									py: '14px',
									borderRadius: '30px',
									bgcolor: '#C85AFF',
									border: '1px solid #9F7AEA',
									'&:hover': { bgcolor: '#B521FF' },
									'&:disabled': { bgcolor: '#E2E8F0', color: '#A0AEC0', border: 'none' },
								}}
							>
								{saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Sign Agreement'}
							</Button>
						</Box>
					</Box>
				</Box>
			</Box>
		)
	}

	// ── VIEW mode ────────────────────────────────────────────────────────────
	return (
		<Box sx={{ minHeight: '100%', bgcolor: '#FFFFFF', display: 'flex', flexDirection: 'column', position: 'relative' }}>
			<BgGradient />
			<Navbar />

			<Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', justifyContent: 'center', p: isMobile ? '16px' : '32px', pb: '48px' }}>
				<Box sx={{ width: '100%', maxWidth: 750, bgcolor: '#FFFFFF', borderRadius: '16px', boxShadow: '0px 4px 20px 0px rgba(0,0,0,0.25)', p: isMobile ? '24px 16px' : '40px 30px', display: 'flex', flexDirection: 'column', gap: '30px' }}>

					{/* Status + signature */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<Box sx={{ width: 49, minHeight: 49, bgcolor: isSigned ? '#B2F5EA' : '#FEFCBF', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
								{isSigned
									? <CheckCircleIcon sx={{ fontSize: 26, color: '#2C7A7B' }} />
									: <HourglassEmptyIcon sx={{ fontSize: 26, color: '#D69E2E' }} />
								}
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '20px', color: isSigned ? '#34A97E' : '#D69E2E', lineHeight: 1.21 }}>
									{isSigned ? 'Agreement Signed' : 'Pending Signature'}
								</Typography>
								{isSigned && signedAgreement?.signed_at && (
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '6px' }}>
										<DrawIcon sx={{ fontSize: 19, color: 'rgba(0,0,0,0.6)' }} />
										<Typography sx={{ fontFamily: 'Inter', fontWeight: 500, fontSize: '16px', color: 'rgba(0,0,0,0.6)' }}>
											Signed {fmt(signedAgreement.signed_at)}
										</Typography>
									</Box>
								)}
							</Box>
						</Box>

						{/* Signature image — when status is 'signed' but no signature
						    file is on record, the merchant marked the agreement
						    as signed in-person, so we surface that explicitly
						    instead of the generic "Signature on file" copy. */}
						<Box sx={{ border: '1px solid rgba(0,0,0,0.3)', borderRadius: '12px', overflow: 'hidden', bgcolor: '#FAFAFA', minHeight: 80 }}>
							{isSigned && signatureUrl ? (
								<Box component="img" src={signatureUrl} alt="Signature" sx={{ display: 'block', maxWidth: '100%', maxHeight: 200, objectFit: 'contain', p: '16px', mx: 'auto' }} />
							) : (
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80 }}>
									<Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#A0AEC0' }}>
										{isSigned
											? (signedAgreement?.signature_path ? 'Signature on file' : 'Signed in-person')
											: 'Not yet signed'}
									</Typography>
								</Box>
							)}
						</Box>
					</Box>

					{agreementInfoJsx}
					{contentBoxJsx()}
				</Box>
			</Box>
		</Box>
	)
}
